import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, catchError, map, tap, finalize } from 'rxjs';
import { Party } from '../../party/party.model';
import { PartyService } from '../../party/party.service';
import { CrmClient, CrmClientCommunication } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { CrmPartyContextService } from '../../shared/crm-client-contact-picker/crm-party-context.service';
import { CommercialPartyReference } from '../../shared/commercial-party-picker/commercial-party.model';

interface SavedCommercialParty { mainParty?: Party | null; subParty?: Party | null; crmClientId?: number; crmAccountId?: number; crmClientContactId?: number; crmClient?: CrmClient | null; crmClientContact?: CrmClientCommunication | null; }

/** Provided by each editor so concurrent pages never share selection state. */
@Injectable()
export class AccountingEditorPartyState {
    party: Party | null = null;
    contact: Party | null = null;
    client: CrmClient | null = null;
    crmContact: CrmClientCommunication | null = null;
    reference: CommercialPartyReference = { source: 'CRM' };
    busy = false;
    error = false;
    private token = 0;
    constructor(private parties: PartyService, private crm: CrmService, private context: CrmPartyContextService) {}

    restore(saved: SavedCommercialParty, companyId: number): void {
        ++this.token;
        this.busy = false; this.error = false;
        this.party = saved.mainParty || null;
        this.contact = saved.subParty?.id && (saved.subParty.id.entityId !== saved.mainParty?.id.entityId || saved.subParty.id.entityType !== saved.mainParty?.id.entityType) ? saved.subParty : null;
        this.client = saved.crmClient || null;
        this.crmContact = saved.crmClientContact || null;
        this.reference = { source: saved.crmClientId ? 'CRM' : 'PARTY', crmClientId: saved.crmClientId, crmAccountId: saved.crmAccountId,
            crmContactId: saved.crmClientContactId, partyEntityId: saved.mainParty?.id.entityId, partyEntityType: saved.mainParty?.id.entityType,
            partyContactEntityId: this.contact?.id.entityId, partyContactEntityType: this.contact?.id.entityType };
        if (saved.crmClientId && !this.client) this.change(this.reference, companyId).subscribe();
    }

    change(reference: CommercialPartyReference, companyId: number): Observable<void> {
        // The picker may emit while its asynchronous directory is being rebuilt.
        if (reference.source === this.reference.source && !reference.crmClientId && !reference.partyEntityId
                && (this.reference.crmClientId || this.reference.partyEntityId)) return of(undefined);
        const sameClient = reference.source === 'CRM' && reference.crmClientId === this.reference.crmClientId;
        if (reference.crmContactId !== this.reference.crmContactId) this.crmContact = null;
        const token = ++this.token;
        this.reference = reference; this.busy = true; this.error = false;
        if (reference.source === 'CRM' && reference.crmClientId) {
            return this.crm.getClient(reference.crmClientId).pipe(tap(response => {
                if (token !== this.token) return;
                if (response.data?.companyId !== companyId) throw new Error('Client company does not match');
                this.client = response.data;
                const party = this.context.partyFromClient(this.client);
                if (party && (!sameClient || !this.party)) { this.party = party; this.contact = null; }
                else if (!sameClient || !this.party) throw new Error('Client has no linked party');
            }), map(() => undefined), catchError(() => { if (token === this.token) this.error = true; return of(undefined); }),
            finalize(() => { if (token === this.token) this.busy = false; }));
        }
        this.client = null;
        if (!reference.partyEntityId || !reference.partyEntityType) { this.party = null; this.contact = null; this.busy = false; return of(undefined); }
        const matches = (party: Party | null, id?: number | null, type?: string | null) => party?.id.entityId === id && party?.id.entityType === type;
        const party = matches(this.party, reference.partyEntityId, reference.partyEntityType) ? of(this.party) : this.parties.getPartyById(companyId, reference.partyEntityType, reference.partyEntityId);
        const contact = reference.partyContactEntityId && reference.partyContactEntityType
            ? matches(this.contact, reference.partyContactEntityId, reference.partyContactEntityType) ? of(this.contact) : this.parties.getPartyById(companyId, reference.partyContactEntityType, reference.partyContactEntityId)
            : of(null);
        return forkJoin({ party, contact }).pipe(tap(result => { if (token === this.token) { this.party = result.party; this.contact = result.contact; this.error = !result.party; } }),
            map(() => undefined), catchError(() => { if (token === this.token) this.error = true; return of(undefined); }), finalize(() => { if (token === this.token) this.busy = false; }));
    }
}
